import { faMusic } from "@fortawesome/free-solid-svg-icons";
import { Box } from "@mui/material";
import { useAtomValue } from "jotai";
import { ReactNode } from "react";
import { useParams } from "react-router-dom";
import { useFetchArtistReleasesQuery } from "../../../api/releases/hooks/useFetchArtistReleasesQuery";
import { editMode } from "../../../atoms/profileScreenEdit";
import { TRACKING_EVENTS_NAME } from "../../../constants/trackingEvents";
import { useGetFrequentlyAskedQuestions } from "../../../hooks/profileScreenHooks/useGetFrequentlyAskedQuestions";
import { useGetUserProfile } from "../../../hooks/profileScreenHooks/useGetUserProfile";
import { useIsVisitingOwnProfile } from "../../../hooks/profileScreenHooks/useIsVisitingOwnProfile";
import { useGetArtistsCompletedProjectsCount } from "../../../hooks/projectHooks/useGetArtistsCompletedProjectsCount";
import { SCREENS } from "../../../routes/screens";
import { TrackStage } from "../../../store/models/project";
import { ReleaseType } from "../../../store/models/release";
import { ScheduledProjectStage } from "../../../store/models/scheduledproject";
import { getMyBookingsRoute } from "../../../store/utils/routeGetters";
import { emitAnalyticsTrackingEvent } from "../../../utils/analyticsUtils";
import { Button, ButtonVariant } from "../../core-ui/components/Button/Button";
import { Text } from "../../core-ui/components/Text/Text";
import { CreateReleaseModal } from "../../screens/BookingsScreen/components/ReleaseTableView/components/CreateReleaseModal";
import { NoReleasesMessage } from "../../screens/BookingsScreen/components/ReleaseTableView/components/NoReleasesMessage";
import { BookingQueryParamKeys } from "../../screens/BookingsScreen/hooks";
import { FrequentlyAskedSectionWrapper } from "../../screens/ProfileScreen/components/FrequentlyAskedSection/FrequentlyAskedSectionWrapper";
import {
  EmptyContainer,
  EmptyContainerFaIcon,
} from "../../screens/ProfileScreen/ProfileScreen.styles";
import { ArtistReleases } from "../ArtistReleases/ArtistReleases";

interface ReleaseOverviewProps {
  onReleaseClick: (releaseId?: number) => void;
  children?: ReactNode;
}

export const ReleaseOverview = ({
  children,
  onReleaseClick,
}: ReleaseOverviewProps) => {
  const { username } = useParams<{ username: string }>();
  const { data: userData } = useGetUserProfile(username);
  const isEditMode = useAtomValue(editMode);
  const isVisitingOwnProfile = useIsVisitingOwnProfile();
  const { data, isPending } = useFetchArtistReleasesQuery({
    releaseType: ReleaseType.NO_RELEASE_TYPE,
    page: 1,
    artistUserId: userData?.id || 0,
  });
  const { data: faqData, isLoading: isInitialLoadingFaqs } =
    useGetFrequentlyAskedQuestions(
      userData?.artist?.verified ? userData?.id : undefined,
    );
  const { data: artistsProjectCountData } = useGetArtistsCompletedProjectsCount(
    {
      enabled: isVisitingOwnProfile,
    },
  );
  const userHasCompletedProjects =
    (artistsProjectCountData?.completed_projects_count ?? 0) > 0;
  const emptyReleaseCTAHref = userHasCompletedProjects
    ? getMyBookingsRoute("projects", {
        [BookingQueryParamKeys.Stages]: ScheduledProjectStage.COMPLETED,
        [BookingQueryParamKeys.TrackStages]: TrackStage.COMPLETED,
      })
    : SCREENS.SEARCH;

  if (!isPending && (!data || data.count === 0)) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "54px",
          textAlign: "center",
        }}
      >
        {isEditMode && !userHasCompletedProjects && (
          <>
            <NoReleasesMessage />
            <CreateReleaseModal />
          </>
        )}
        {isEditMode && userHasCompletedProjects && (
          <EmptyContainer>
            <EmptyContainerFaIcon icon={faMusic} />
            <Text>
              Select a completed project on EngineEars to feature on your
              profile here
            </Text>
            <Button
              variant={ButtonVariant.OUTLINED}
              href={emptyReleaseCTAHref}
              onClick={() => {
                emitAnalyticsTrackingEvent(
                  TRACKING_EVENTS_NAME.USER_CLICKED_EMPTY_RELEASES_CTA,
                  { did_user_have_zero_completed_projects: false },
                );
              }}
            >
              Select a Project to Publish
            </Button>
          </EmptyContainer>
        )}
        {!isEditMode && (
          <EmptyContainer>
            <EmptyContainerFaIcon icon={faMusic} />
            <Text>No releases for this artist available</Text>
          </EmptyContainer>
        )}
        {userData?.artist?.verified && (
          <FrequentlyAskedSectionWrapper
            faqData={faqData}
            isInitialLoadingFaqs={isInitialLoadingFaqs}
            editModeText={
              "Adding Frequently Asked Questions will help your listeners find what they need."
            }
          />
        )}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "54px",
        textAlign: "center",
      }}
    >
      {children}
      <ArtistReleases onReleaseClick={onReleaseClick} />
      {userData?.artist?.verified && (
        <FrequentlyAskedSectionWrapper
          faqData={faqData}
          isInitialLoadingFaqs={isInitialLoadingFaqs}
          editModeText={
            "Adding Frequently Asked Questions will help your listeners find what they need."
          }
        />
      )}
    </Box>
  );
};
